import { inject, injectable } from 'inversify'
import { computed, makeObservable } from 'mobx'
import type { Capability } from '@shared/index'
import type { AppLifecycle } from '@core/interfaces/app-lifecycle'
import { I18nRepository } from '@core/infra/i18n/i18n.repository'
import { Router } from '@core/routing/router'
import { AdminModeRepository } from './admin-mode.repository'
import { UserModel } from './user.model'

/**
 * Presentation policy for the top-bar admin-mode switch: which capabilities
 * make the switch worth showing at all. Deliberately not on
 * AdminModeRepository — whether to *offer* the switch is a display decision,
 * the same split `FeedbackConfig`/`FeedbackRepository` make. Mirrors exactly
 * the `requiresCapability` values the admin routes carry
 * (router.config.ts) — one admin route, one entry here.
 */
const ADMIN_CAPABILITIES: readonly Capability[] = ['users.manage', 'access.inspect', 'errors.read', 'audit.read']

/**
 * Drives the top-bar switch (mounted beside ThemeToggleComponent /
 * LanguageSwitcherComponent) and restores its saved state at boot — same
 * dual role as ThemePresenter for ThemeRepository.
 */
@injectable()
export class AdminModeTogglePresenter implements AppLifecycle {
  @inject(AdminModeRepository)
  private adminModeRepository!: AdminModeRepository

  @inject(UserModel)
  private userModel!: UserModel

  @inject(I18nRepository)
  private i18nRepository!: I18nRepository

  @inject(Router)
  private router!: Router

  constructor() {
    // toggle is no longer annotated `action` here: it's async and its only
    // observable write is `setAdminMode`, which is already its own action
    // (admin-mode.repository.ts) — actions don't span an await regardless.
    makeObservable(this, {
      viewModel: computed,
    })
  }

  initialize(): void {
    this.adminModeRepository.restore()
  }

  get viewModel(): { showToggle: boolean; enabled: boolean; label: string; ariaLabel: string } {
    const enabled = this.adminModeRepository.adminModePm
    return {
      showToggle: ADMIN_CAPABILITIES.some((capability) => this.userModel.can(capability)),
      enabled,
      label: this.i18nRepository.t('shell.adminMode'),
      ariaLabel: this.i18nRepository.t(enabled ? 'shell.adminModeOn' : 'shell.adminModeOff'),
    }
  }

  toggle = async (): Promise<void> => {
    const next = !this.adminModeRepository.adminModePm

    // Turning off while standing on the page the switch just hid — the
    // switch hiding the nav entry is not enough, it has to leave the page too.
    // Navigate first and check the result (goToId's own contract, router.ts):
    // leaving can be refused (unsaved work), and a refused exit must not flip
    // the switch — otherwise admin mode reads off on a page only admin mode
    // reaches.
    if (!next && this.router.currentRoute.routeDef?.requiresAdminMode) {
      if (!(await this.router.goToId('Home'))) return
    }

    this.adminModeRepository.setAdminMode(next)
  }
}
